// Frame storage service for managing captured frames
import { VideoFormat } from '@/lib/utils'
import { LOCAL_LIBRARY_ID } from '@/lib/browser-persistence'

// Frame capture model
export interface CapturedFrame {
  id: string
  videoId: string
  videoName: string
  frameNumber: number
  timestamp: number // Time in seconds in the video
  captureDate: string
  userId: string
  imageData: string // Base64 encoded PNG (local cache)
  thumbnailData?: string // Smaller version for display
  width: number
  height: number
  fileSize: number // Size in bytes
  format: VideoFormat | null
  // Optional source metadata for legacy/imported frames.
  remoteId?: string
  blobUrl?: string
  remoteUrl?: string // Short-lived signed read URL
}

// Frame filter options
export interface FrameFilterOptions {
  searchTerm?: string
  videoId?: string
  frameNumberMin?: number
  frameNumberMax?: number
  dateFrom?: string
  dateTo?: string
  sortBy?: 'frameNumber' | 'captureDate' | 'videoName'
  sortOrder?: 'asc' | 'desc'
}

const DB_NAME = 'framex-frames-db'
const DB_VERSION = 1
const FRAMES_STORE = 'frames'
const FRAMES_INDEX_USER = 'userId'
const FRAMES_INDEX_VIDEO = 'videoId'
const FRAMES_INDEX_COMPOSITE = 'videoId_frameNumber'

class FramesStorageService {
  private db: IDBDatabase | null = null

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)

      request.onerror = () => {
        console.error('Failed to open Frames IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create object store for frames
        if (!db.objectStoreNames.contains(FRAMES_STORE)) {
          const frameStore = db.createObjectStore(FRAMES_STORE, { keyPath: 'id' })
          frameStore.createIndex(FRAMES_INDEX_USER, 'userId', { unique: false })
          frameStore.createIndex(FRAMES_INDEX_VIDEO, 'videoId', { unique: false })
          frameStore.createIndex(FRAMES_INDEX_COMPOSITE, ['videoId', 'frameNumber'], { unique: true })
        }
      }
    })
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.db) {
      await this.initialize()
    }
  }

  // Store a captured frame
  async storeFrame(frame: CapturedFrame): Promise<void> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readwrite')
    const store = transaction.objectStore(FRAMES_STORE)

    return new Promise((resolve, reject) => {
      const request = store.put(frame)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // Get a frame by ID
  async getFrame(frameId: string): Promise<CapturedFrame | null> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readonly')
    const store = transaction.objectStore(FRAMES_STORE)

    return new Promise((resolve, reject) => {
      const request = store.get(frameId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || null)
    })
  }

  // Get frame by video ID and frame number (unique combination)
  async getFrameByVideoAndNumber(videoId: string, frameNumber: number): Promise<CapturedFrame | null> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readonly')
    const store = transaction.objectStore(FRAMES_STORE)
    const index = store.index(FRAMES_INDEX_COMPOSITE)

    return new Promise((resolve, reject) => {
      const request = index.get([videoId, frameNumber])
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || null)
    })
  }

  // Get all frames for a video (sorted by frame number)
  async getFramesByVideo(videoId: string): Promise<CapturedFrame[]> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readonly')
    const store = transaction.objectStore(FRAMES_STORE)
    const index = store.index(FRAMES_INDEX_VIDEO)

    return new Promise((resolve, reject) => {
      const request = index.getAll(videoId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const frames = request.result || []
        // Sort by frame number
        frames.sort((a, b) => a.frameNumber - b.frameNumber)
        resolve(frames)
      }
    })
  }

  // Get all frames for a user
  async getFramesByUser(userId: string): Promise<CapturedFrame[]> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readonly')
    const store = transaction.objectStore(FRAMES_STORE)
    const index = store.index(FRAMES_INDEX_USER)

    return new Promise((resolve, reject) => {
      const request = index.getAll(userId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        const frames = request.result || []
        resolve(frames)
      }
    })
  }

  // Get frame numbers that have been captured for a video
  async getCapturedFrameNumbers(videoId: string): Promise<number[]> {
    const frames = await this.getFramesByVideo(videoId)
    return frames.map(f => f.frameNumber)
  }

  // Delete a frame
  async deleteFrame(frameId: string): Promise<void> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readwrite')
    const store = transaction.objectStore(FRAMES_STORE)

    return new Promise((resolve, reject) => {
      const request = store.delete(frameId)
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve()
    })
  }

  // Delete multiple frames
  async deleteFrames(frameIds: string[]): Promise<void> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readwrite')
    const store = transaction.objectStore(FRAMES_STORE)

    return new Promise((resolve, reject) => {
      let completed = 0
      let hasError = false

      frameIds.forEach(id => {
        const request = store.delete(id)
        request.onerror = () => {
          if (!hasError) {
            hasError = true
            reject(request.error)
          }
        }
        request.onsuccess = () => {
          completed++
          if (completed === frameIds.length && !hasError) {
            resolve()
          }
        }
      })

      if (frameIds.length === 0) resolve()
    })
  }

  // Delete all frames for a video
  async deleteFramesByVideo(videoId: string): Promise<number> {
    const frames = await this.getFramesByVideo(videoId)
    await this.deleteFrames(frames.map(f => f.id))
    return frames.length
  }

  // Delete all frames for a user
  async deleteFramesByUser(userId: string): Promise<number> {
    const frames = await this.getFramesByUser(userId)
    await this.deleteFrames(frames.map(f => f.id))
    return frames.length
  }

  // Filter frames with various options
  async filterFrames(userId: string, options: FrameFilterOptions = {}): Promise<CapturedFrame[]> {
    let frames = await this.getFramesByUser(userId)

    // Apply filters
    if (options.videoId) {
      frames = frames.filter(f => f.videoId === options.videoId)
    }

    if (options.searchTerm) {
      const term = options.searchTerm.toLowerCase()
      frames = frames.filter(f => 
        f.videoName.toLowerCase().includes(term) ||
        f.frameNumber.toString().includes(term)
      )
    }

    if (options.frameNumberMin !== undefined) {
      frames = frames.filter(f => f.frameNumber >= options.frameNumberMin!)
    }

    if (options.frameNumberMax !== undefined) {
      frames = frames.filter(f => f.frameNumber <= options.frameNumberMax!)
    }

    if (options.dateFrom) {
      const fromDate = new Date(options.dateFrom).getTime()
      frames = frames.filter(f => new Date(f.captureDate).getTime() >= fromDate)
    }

    if (options.dateTo) {
      const toDate = new Date(options.dateTo).getTime()
      frames = frames.filter(f => new Date(f.captureDate).getTime() <= toDate)
    }

    // Apply sorting
    const sortBy = options.sortBy || 'frameNumber'
    const sortOrder = options.sortOrder || 'asc'
    const multiplier = sortOrder === 'asc' ? 1 : -1

    frames.sort((a, b) => {
      switch (sortBy) {
        case 'frameNumber':
          return (a.frameNumber - b.frameNumber) * multiplier
        case 'captureDate':
          return (new Date(a.captureDate).getTime() - new Date(b.captureDate).getTime()) * multiplier
        case 'videoName':
          return a.videoName.localeCompare(b.videoName) * multiplier
        default:
          return 0
      }
    })

    return frames
  }

  // Get storage statistics for frames
  async getFrameStorageStats(userId: string): Promise<{ count: number; totalSize: number }> {
    const frames = await this.getFramesByUser(userId)
    return {
      count: frames.length,
      totalSize: frames.reduce((sum, f) => sum + f.fileSize, 0)
    }
  }

  // Get all frames (for admin or debugging)
  async getAllFrames(): Promise<CapturedFrame[]> {
    await this.ensureInitialized()
    if (!this.db) throw new Error('Database not initialized')

    const transaction = this.db.transaction([FRAMES_STORE], 'readonly')
    const store = transaction.objectStore(FRAMES_STORE)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onerror = () => reject(request.error)
      request.onsuccess = () => resolve(request.result || [])
    })
  }
}

// Singleton instance
export const framesStorageService = new FramesStorageService()

// Helper function to create a thumbnail from image data
export function createThumbnail(imageData: string, maxSize: number = 200): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let width = img.width
      let height = img.height

      // Calculate scaled dimensions
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width
          width = maxSize
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height
          height = maxSize
        }
      }

      canvas.width = width
      canvas.height = height

      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }

      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', 0.7))
    }
    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = imageData
  })
}

// Helper function to convert canvas to base64 data URL
export function canvasToDataUrl(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/png')
}

// Helper function to estimate file size from data URL
export function estimateDataUrlSize(dataUrl: string): number {
  // Remove data URL header and calculate base64 size
  const base64 = dataUrl.split(',')[1] || dataUrl
  // Base64 encoding adds ~33% overhead, so actual file size is ~75% of string length
  return Math.round((base64.length * 3) / 4)
}

// Capture a frame and store it in this browser for fast offline access.
export async function captureAndStoreFrame(
  canvas: HTMLCanvasElement,
  videoId: string,
  videoName: string,
  frameNumber: number,
  timestamp: number,
  userId: string = LOCAL_LIBRARY_ID,
  format: VideoFormat | null
): Promise<CapturedFrame> {
  // Check if frame already exists locally
  const existing = await framesStorageService.getFrameByVideoAndNumber(videoId, frameNumber)
  if (existing) {
    console.log(`Frame ${frameNumber} already captured for video ${videoId}`)
    return existing
  }

  // Synchronous pixel work, keep this minimal, no awaits between toDataURL
  // and the IndexedDB write so the user sees instant feedback.
  const imageData = canvasToDataUrl(canvas)
  const fileSize = estimateDataUrlSize(imageData)
  const thumbnailData = await createThumbnail(imageData, 200)

  const frame: CapturedFrame = {
    id: crypto.randomUUID(),
    videoId,
    videoName,
    frameNumber,
    timestamp,
    captureDate: new Date().toISOString(),
    userId: userId || LOCAL_LIBRARY_ID,
    imageData,
    thumbnailData,
    width: canvas.width,
    height: canvas.height,
    fileSize,
    format,
  }

  await framesStorageService.storeFrame(frame)

  return frame
}

export function getRemoteSyncBacklog(): number {
  return 0
}
